import { useState } from 'react';
import { useHumansInSpace } from '../hooks/useHumansInSpace';
import { useEnrichedCrew } from '../hooks/useEnrichedCrew';

// Ambient pill that lives in the TopBar. Reads "10 IN SPACE" with a small
// human-silhouette glyph. Click opens a flyout listing the crew with photos.
// Click any astronaut to see their bio, agency, time in space, and links.
// Flashes for ~10s when count changes (rare: launches, dockings, EVAs).

const MONO = 'var(--mono)';
// Agency color coding (matches our existing palette)
const AGENCY_COLOR = {
  NASA:    '#5a8aff',
  RFSA:    '#ff6060',
  ESA:     '#3b82f6',
  JAXA:    '#fbbf24',
  ISRO:    '#f97316',
  CASC:    '#dc2626',  // China
  CMSE:    '#dc2626',
  SpaceX:  '#a78bfa',
  ASA:     '#06b6d4',
};

function agencyColor(abbrev) {
  return AGENCY_COLOR[abbrev] || '#7a9aba';
}

function PersonGlyph({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" style={{ verticalAlign: '-1px' }}>
      <circle cx="8" cy="4" r="2.4" fill="currentColor" />
      <path d="M8 7.2 C5.5 7.2 3.6 9 3.6 11.6 V14 H12.4 V11.6 C12.4 9 10.5 7.2 8 7.2 Z" fill="currentColor" />
    </svg>
  );
}

function timeAgo(iso) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Flag emoji from alpha-2 country code
function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return '';
  const A = 0x1F1E6;
  const codes = cc.toUpperCase().split('').map(c => A + c.charCodeAt(0) - 65);
  return String.fromCodePoint(...codes);
}

// Find enriched record by best-effort name match (case-insensitive, ignores
// punctuation/diacritics). Falls back to null if no match.
function matchEnriched(plainName, enrichedList) {
  if (!plainName) return null;
  const norm = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z\s]/g, '').trim();
  const target = norm(plainName);
  return enrichedList.find(e => norm(e.name) === target)
      || enrichedList.find(e => norm(e.name).includes(target) || target.includes(norm(e.name)))
      || null;
}

// Detail panel that slides over the flyout list when a person is selected.
function CrewDetail({ person, enriched, craft, onBack }) {
  const e = enriched;
  const ac = agencyColor(e?.agency_abbrev);
  const hasImage = !!e?.image_url;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <button
        onClick={onBack}
        style={{
          background: 'none', border: 'none', color: 'var(--cyan)',
          fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em',
          textAlign: 'left', padding: '0 0 10px 0', cursor: 'pointer',
        }}
      >← BACK</button>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        {hasImage ? (
          <img
            src={e.image_url}
            alt={person.name || e?.name}
            style={{
              width: 80, height: 80, objectFit: 'cover',
              borderRadius: 3, border: `1px solid ${ac}66`,
              flexShrink: 0,
            }}
          />
        ) : (
          <div style={{
            width: 80, height: 80, borderRadius: 3,
            border: `1px solid ${ac}66`,
            background: 'var(--bg-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: ac, flexShrink: 0,
          }}>
            <PersonGlyph size={40} />
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            color: 'var(--txt-bright)', fontWeight: 700, fontSize: 12,
            fontFamily: MONO, marginBottom: 2,
          }}>{e?.name || person.name}</div>
          {e?.nationality && (
            <div style={{ color: 'var(--txt-dim)', fontSize: 10, fontFamily: MONO, marginBottom: 4 }}>
              {flagEmoji(e.country_code)} {e.nationality}
              {e.age ? ` · age ${e.age}` : ''}
            </div>
          )}
          <div style={{
            display: 'inline-block', padding: '2px 6px',
            background: `${ac}22`, border: `0.5px solid ${ac}55`,
            borderRadius: 2, color: ac,
            fontFamily: MONO, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.1em',
          }}>{e?.agency_abbrev || craft}</div>
        </div>
      </div>

      {/* Stats */}
      {e && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
          marginBottom: 12, fontFamily: MONO, fontSize: 10,
        }}>
          {e.time_in_space && (
            <Stat label="TIME IN SPACE" value={e.time_in_space} />
          )}
          {e.eva_time && e.eva_time !== '<1h' && (
            <Stat label="EVA TIME" value={e.eva_time} />
          )}
          {e.flights_count > 0 && (
            <Stat label="FLIGHTS" value={e.flights_count} />
          )}
          {e.spacewalks_count > 0 && (
            <Stat label="SPACEWALKS" value={e.spacewalks_count} />
          )}
        </div>
      )}

      {/* Bio */}
      {e?.bio && (
        <div style={{
          color: 'var(--txt)', fontSize: 11, lineHeight: 1.55,
          marginBottom: 12, maxHeight: 220, overflowY: 'auto',
          fontFamily: 'var(--sans)',
        }}>{e.bio}</div>
      )}

      {/* Links */}
      {e && (e.wiki || e.twitter_url || e.instagram_url) && (
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap',
          paddingTop: 8, borderTop: '1px solid var(--hair)',
        }}>
          {e.wiki && (
            <LinkBadge href={e.wiki} label="WIKI" />
          )}
          {e.twitter_url && (
            <LinkBadge href={e.twitter_url} label="X / TWITTER" />
          )}
          {e.instagram_url && (
            <LinkBadge href={e.instagram_url} label="INSTAGRAM" />
          )}
        </div>
      )}

      {!e && (
        <div style={{
          color: 'var(--txt-dim)', fontSize: 10, fontStyle: 'italic',
          fontFamily: MONO, padding: '20px 0', textAlign: 'center',
        }}>
          No enriched data available for this astronaut.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{
      padding: '6px 8px',
      background: 'var(--bg-2)',
      border: '1px solid var(--hair)',
      borderRadius: 2,
    }}>
      <div style={{ color: 'var(--txt-dim)', fontSize: 8, letterSpacing: '0.1em' }}>{label}</div>
      <div style={{ color: 'var(--txt)', fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function LinkBadge({ href, label }) {
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer"
      style={{
        padding: '4px 8px', background: 'var(--amber-2)',
        border: '1px solid rgba(233,179,93,0.3)', borderRadius: 2,
        color: 'var(--cyan)', fontFamily: MONO, fontSize: 9, fontWeight: 700,
        letterSpacing: '0.08em', textDecoration: 'none',
      }}
    >{label} ↗</a>
  );
}

// Derive craft from agency abbrev. CNSA/CMSE = Tiangong, everything else = ISS.
function craftForAgency(abbrev) {
  if (abbrev === 'CNSA' || abbrev === 'CMSE') return 'Tiangong';
  return 'ISS';
}

export default function HumansInSpacePill({ compact = false }) {
  // Open Notify is stale; Launch Library 2 (via /api/crew/enriched) is the
  // source of truth for both count and roster. Open-notify hook retained
  // only for the change-flash animation (prevCount/justChanged).
  const { prevCount, justChanged } = useHumansInSpace();
  const { crew: enrichedCrew, fetchedAt: enrichedFetchedAt } = useEnrichedCrew();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const count = enrichedCrew.length || null;
  const fetched_at = enrichedFetchedAt;
  const display = count == null ? '?' : String(count);

  // Group enriched crew by inferred craft
  const byCraft = {};
  for (const e of enrichedCrew) {
    const craft = craftForAgency(e.agency_abbrev);
    (byCraft[craft] = byCraft[craft] || []).push({ name: e.name, craft, enriched: e });
  }
  const crafts = Object.keys(byCraft).sort();

  function handleClose() {
    setOpen(false);
    setSelected(null);
  }

  const selectedEnriched = selected ? selected.person.enriched : null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        className="og-crew-trigger" aria-label="Crew in space" aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        title={`${display} humans in space (updated ${timeAgo(fetched_at)})`}
        style={{
          background: justChanged ? 'var(--amber-2)' : 'var(--bg-2)',
          border: `1px solid ${justChanged ? 'var(--amber)' : 'var(--hair-2)'}`,
          borderRadius: 3,
          padding: compact ? '4px 6px' : '4px 10px', minHeight: 44,
          color: justChanged ? 'var(--amber)' : 'var(--accent)',
          fontFamily: MONO,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
          transition: 'background 300ms ease, border-color 300ms ease, color 300ms ease',
        }}
      >
        <PersonGlyph size={12} />
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{display}</span>
        <span style={{ opacity: 0.8 }}>{compact ? 'CREW' : 'IN SPACE'}</span>
        {justChanged && prevCount != null && (
          <span style={{ fontSize: 9, opacity: 0.85 }}>
            {count > prevCount ? `+${count - prevCount}` : `${count - prevCount}`}
          </span>
        )}
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: 'calc(56px + env(safe-area-inset-top))', right: 8, zIndex: 120,
            width: 'min(340px, calc(100vw - 16px))', maxHeight: 'min(520px, calc(100dvh - 72px - env(safe-area-inset-top) - env(safe-area-inset-bottom)))',
            background: 'rgba(10,10,12,0.97)',
            border: '1px solid var(--hair-2)',
            borderRadius: 4,
            padding: '12px 14px',
            color: 'var(--txt)',
            fontFamily: MONO,
            fontSize: 11,
            backdropFilter: 'blur(8px)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginBottom: 10,
            flexShrink: 0,
          }}>
            <span style={{ color: 'var(--amber)', fontWeight: 800, letterSpacing: '0.1em' }}>
              {count ?? '?'} HUMANS IN SPACE
            </span>
            <span style={{ color: 'var(--txt-mute)', fontSize: 10 }}>
              {timeAgo(fetched_at)}
            </span>
          </div>

          {/* Detail view */}
          {selected && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <CrewDetail
                person={selected.person}
                enriched={selectedEnriched}
                craft={selected.craft}
                onBack={() => setSelected(null)}
              />
            </div>
          )}

          {/* List view */}
          {!selected && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {crafts.length === 0 && (
                <div style={{ color: 'var(--txt-mute)', fontStyle: 'italic' }}>
                  No data yet, backend still fetching.
                </div>
              )}

              {crafts.map(craft => (
                <div key={craft} style={{ marginBottom: 10 }}>
                  <div style={{
                    color: 'var(--cyan)', fontWeight: 700, fontSize: 10,
                    letterSpacing: '0.08em', marginBottom: 4,
                  }}>{craft} ({byCraft[craft].length})</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {byCraft[craft].map(person => {
                      const e = person.enriched;
                      return (
                        <div
                          key={person.name}
                          onClick={() => setSelected({ person, craft })}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '5px 6px', borderRadius: 2,
                            cursor: 'pointer',
                            background: 'rgba(232,227,216,0.03)',
                            border: '1px solid var(--hair)',
                          }}
                        >
                          {e?.thumbnail_url ? (
                            <img
                              src={e.thumbnail_url}
                              alt=""
                              style={{
                                width: 28, height: 28, objectFit: 'cover',
                                borderRadius: 2, flexShrink: 0,
                              }}
                            />
                          ) : (
                            <div style={{
                              width: 28, height: 28, borderRadius: 2,
                              background: 'var(--bg-2)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: 'var(--txt-mute)', flexShrink: 0,
                            }}>
                              <PersonGlyph size={14} />
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              color: 'var(--txt)', fontSize: 11,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{person.name}</div>
                            {e?.agency_abbrev && (
                              <div style={{
                                color: agencyColor(e.agency_abbrev), fontSize: 9,
                                letterSpacing: '0.08em', fontWeight: 600,
                              }}>{e.agency_abbrev}{e.country_code ? ` · ${flagEmoji(e.country_code)}` : ''}</div>
                            )}
                          </div>
                          <span style={{ color: 'var(--txt-mute)', fontSize: 14 }}>›</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div style={{
                marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--hair)',
                color: 'var(--txt-mute)', fontSize: 9, fontStyle: 'italic',
              }}>
                Sources: open-notify · launchlibrary.net
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
